<template>
  <header class="header" :class="{ 'widescreen-padding': display.lgAndUp }">
    <div class="first-line">
      <div class="logo-section">
        <img src="/assets/logo-text-only.png" alt="Menu Icon" :class="display.mdAndUp ? 'logo-text' : 'logo-text-small'"
          @click="logoClick" />
      </div>
      <div class="logo-section right-actions">
        <!-- Rank pill (revamp §3): Lv.N + 영어 레벨 name. Pre-Phase-C everyone
             is honestly Lv.1 스태프 (Eddie-locked: show, don't hide). Tap →
             ladder popover. Child-combinator scoping per the paid-for lesson. -->
        <button class="hrank" type="button" :aria-expanded="ladderOpen" @click.stop="ladderOpen = !ladderOpen">
          <b>Lv.{{ rank.lv }}</b><span class="hrank-name">{{ rank.name }}</span>
        </button>
        <!-- No streak or counter chips here (2026-07-30).
             Two unlabelled icons in a corner communicated nothing — Eddie could
             not identify either, and he built them. The attendance streak went
             entirely: it rewards opening the app, which can rise while a member
             learns nothing, and the ⚡ drill streak already tracks the behaviour
             worth celebrating, on the drill button where it has context. The
             실전 count moved into the ⓘ panel's 실전 사용 row, which already has a
             label, a description and a drawer.
             NOTE: pageViews is still written by the router — adminAnalytics
             reads it for last-active, so that collection and its rules block
             stay regardless. -->
        <button class="menu-icon" @click="toggleMenu">
          <MemberAvatar
            :avatar-url="avatarUrl"
            :name="avatarName"
            :size="30"
            class="menu-image-wrap"
          />
          <img :src="getRankPicturePath" alt="Menu Icon" class="rank-image" />
        </button>
      </div>
      <!-- Level ladder popover (spec §3): compact climbing track, bottom-up.
           Passed = ✓ dot · current = 지금 여기 · next = dotted ring · locked =
           ghost. Visible but quiet — aspiration without discouragement. -->
      <div v-if="ladderOpen" class="hladder-scrim" @click="ladderOpen = false"></div>
      <div v-if="ladderOpen" class="hladder" role="dialog" aria-label="영어 레벨">
        <p class="hladder-head">영어 레벨</p>
        <ol class="hladder-list">
          <li
            v-for="r in ladderDesc"
            :key="r.lv"
            class="hladder-row"
            :class="{ 'hladder-row--now': r.lv === rank.lv, 'hladder-row--past': r.lv < rank.lv }"
          >
            <span class="hladder-dot" aria-hidden="true">{{ r.lv < rank.lv ? '✓' : '' }}</span>
            <span class="hladder-name">{{ r.name }}</span>
            <span v-if="r.lv === rank.lv" class="hladder-here">지금 여기</span>
            <span v-else class="hladder-th">복습한 표현 {{ r.threshold }}개</span>
          </li>
        </ol>
      </div>
      <div class="menu" v-if="isMenuVisible">
        <nav>
          <!--
          <a @click="goToHome">Home</a>
          <a href="#" @click="toggleMenu">About</a>
          <a href="#" @click="toggleMenu">Contact</a>
          <div class="divider"></div>
          -->
          <div class="version">v0.0.4</div>
          <div class="welcome">안녕하세요,</div>
          <div class="user-info">{{ userInfo?.fname }} {{ userInfo?.lname }}</div>
          <div class="divider"></div>
          <a v-if="userInfo.tier === 'admin'" @click="goToDashboard" class="action-btn">Dashboard</a>
          <a v-if="userInfo.tier === 'admin'" @click="manageInvites" class="action-btn">Invites</a>
          <a v-if="userInfo.tier === 'admin'" @click="goToAnalytics" class="action-btn">Analytics</a>
          <a v-if="userInfo.tier === 'admin'" @click="goToMemberships" class="action-btn">Memberships</a>
          <a v-if="userInfo.tier === 'admin'" @click="goToMissions" class="action-btn">Missions</a>
          <a @click="goToProfileEdit" class="action-btn">프로필 편집</a>
          <a @click="goToPaymentManage" class="action-btn">결제 관리</a>
          <a @click="showFeedback" class="action-btn">피드백 하기</a>
          <a @click="signOut" class="action-btn">로그아웃</a>
        </nav>
      </div>
    </div>
  </header>
  <!-- Global pill nav — replaces the old left navigation. Hidden for tier-0
       (they only have 표현집 and shouldn't see surfaces they can't reach). -->
  <HomePillNav
    v-if="showPills"
    :activeLens="currentLens"
    @change="onPillChange"
  />
  <v-divider style="width: 100%;"></v-divider>
  <!-- Login View Modal -->
  <v-dialog v-model="showFeedbackPopUp" max-width="710px" max-height="600px">
    <FeedbackPopUpCard :userInfo="userInfo" @triggerCloseButton="showFeedbackPopUp = false" @showError="showError" />
  </v-dialog>

</template>

<script>
import { auth } from "@/firebaseConfig";
import userService from "@/api/userService";

import FeedbackPopUpCard from "@/components/v-cards/FeedbackPopUpCard.vue";
import HomePillNav from "@/components/nav/HomePillNav.vue";
import MemberAvatar from "@/components/profile/MemberAvatar.vue";
import { fullName } from "@/utils/community";
import { rankFor, RANK_LADDER } from "@/services/homeEngine";
import { masteredCount } from "@/services/masteredStore";

import { useDisplay } from 'vuetify';

export default {
  components: {
    FeedbackPopUpCard,
    HomePillNav,
    MemberAvatar,
  },
  data() {
    return {
      userInfo: null,
      errorMessage: "",
      isMenuVisible: false,
      display: {},
      showFeedbackPopUp: false,
      ladderOpen: false,
    };
  },
  async mounted() {
    this.display = useDisplay();
    this.userInfo = await userService.getUser(auth.currentUser.uid);
  },
  computed: {
    isOwner() {
      return auth.currentUser?.email === 'sung.ws.lee@gmail.com';
    },
    /**
     * Rank from the REAL 복습한-표현 count, via masteredStore.
     *
     * Was `rankFor(0)` hardcoded, with a comment saying 0 was honest because no
     * drill existed. It shipped, the comment stayed, and every member remained
     * Lv.1 스태프 no matter what they earned. Phase-gated constants need a
     * revisit marker, not an explanation — see ENGINE_SPEC_V2 §13.
     */
    rank() {
      return rankFor(masteredCount());
    },
    /** Ladder rows top-down (파트너 first) for the climbing-track popover. */
    ladderDesc() {
      return [...RANK_LADDER].reverse();
    },
    // Tier-0 has only the 표현집 page — pill nav has nothing useful to offer.
    showPills() {
      return !!this.userInfo && this.userInfo.tier !== '0';
    },
    currentLens() {
      const p = this.$route.path;
      if (p.startsWith('/home')) return 'home';
      if (p.startsWith('/community')) return 'community';
      if (p.startsWith('/library')) return 'library';
      if (p.startsWith('/books')) return 'books';
      return '';
    },
    // Avatar parity with /community + /profile/edit: prefer the uploaded
    // avatarUrl (the screenshotable photo members manage themselves) and
    // fall back to MemberAvatar's initials. The legacy `picture` (preset
    // YoungPro brand PNGs) is intentionally NOT used here — the goal is
    // that whichever avatar a member set in 프로필 편집 is the same one
    // shown in the top-right of every page.
    avatarUrl() {
      return this.userInfo?.avatarUrl || '';
    },
    avatarName() {
      return fullName(this.userInfo);
    },
    getRankPicturePath() {
      if (this.userInfo?.points && this.userInfo?.points > 150) {
        return '/assets/ranks/rank1.png';
      } else if (this.userInfo?.points && this.userInfo?.points > 80) {
        return '/assets/ranks/rank2.png';
      }

      return '/assets/ranks/rank3.png';
    }
  },
  methods: {
    async logoClick() {
      if(this.$route.name === 'Payment') {
        this.$router.push('/');
      } else {
        this.$router.push('/home');
      }
    },
    toggleMenu() {
      this.isMenuVisible = !this.isMenuVisible;
    },
    async signOut() {
      this.errorMessage = ""; // Clear any previous error messages
      try {
        await auth.signOut();
        this.$router.push("/"); // Redirect to the login page
      } catch (error) {
        this.errorMessage = error.message;
        console.error("Sign out error:", error);
        this.isMenuVisible = false;
      }
    },
    // All four pills now live at clean top-level URLs — `/home`,
    // `/community`, `/archive`, `/train`. No more `/m/` prefix.
    onPillChange(lens) {
      // 자료실 is the one lens whose route needs a segment — /books alone is
      // not a route, and the router bounces unknown categories back to 표현집.
      // First member pill of the 자료실 switcher (label 세션 다시보기; the
      // route value is the stable pre-rename segment).
      const path = lens === 'books' ? '/books/라이브 다시보기' : `/${lens}`;
      if (this.$route.path !== path) this.$router.push(path);
    },
    showFeedback() {
      this.showFeedbackPopUp = true;
    },
    manageInvites() {
      this.isMenuVisible = false;
      this.$router.push('/admin/invites');
    },
    goToAnalytics() {
      this.isMenuVisible = false;
      this.$router.push('/admin/analytics');
    },
    goToMemberships() {
      this.isMenuVisible = false;
      this.$router.push('/admin/memberships');
    },
    goToMissions() {
      this.isMenuVisible = false;
      this.$router.push('/admin/missions');
    },
    goToDashboard() {
      this.isMenuVisible = false;
      this.$router.push('/admin/dashboard');
    },
    goToProfileEdit() {
      this.isMenuVisible = false;
      this.$router.push('/profile/edit');
    },
    goToPaymentManage() {
      this.isMenuVisible = false;
      this.$router.push('/payment/manage');
    },
  },
};
</script>

<style scoped>
/* ── Revamp header chips ──────────────────────────────────────────────
   Rank pill: `.hrank > b` — CHILD combinator only (the descendant version
   leaked the pill onto ladder rows; paid-for lesson, spec §3). */
.hrank {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px 5px 5px;
  border: 1px solid var(--hairline);
  border-radius: 999px;
  background: var(--surface);
  cursor: pointer;
  font: inherit;
}
.hrank > b {
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--accent-strong);   /* white on #C83C10 = 5.11:1 (was 3.58) */
  color: #fff;
  font-size: 11px;
  font-weight: 800;
}
.hrank-name { font-size: 12.5px; font-weight: 700; color: var(--text-primary); }
.hfire {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 5px 10px;
  border-radius: 999px;
  background: var(--accent-soft-bg);
  font-size: 12px;
  font-weight: 700;
  color: var(--accent-strong);
}
.hladder-scrim { position: fixed; inset: 0; z-index: 40; }
.hladder {
  position: absolute;
  top: 52px;
  right: 84px;
  z-index: 41;
  width: 200px;
  background: var(--surface);
  border: 1px solid var(--hairline);
  border-radius: 14px;
  padding: 12px 14px;
  box-shadow: 0 18px 40px -18px rgba(25, 26, 30, 0.35);
}
.hladder-head { margin: 0 0 8px; font-size: 11px; font-weight: 700; color: var(--text-tertiary); }
.hladder-list { list-style: none; margin: 0; padding: 0; }
.hladder-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  color: var(--text-tertiary);
}
.hladder-dot {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border: 1.5px dashed var(--hairline);
  border-radius: 50%;
  font-size: 9px;
  color: var(--text-primary);
}
.hladder-row--past { color: var(--text-secondary); }
.hladder-row--past .hladder-dot { background: var(--accent); border: none; }
.hladder-row--now {
  color: var(--accent-strong);
  background: var(--accent-soft-bg);
  border-radius: 8px;
  margin: 0 -6px;
  padding: 5px 6px;
}
.hladder-row--now .hladder-dot { border: 1.5px dotted var(--accent); }
.hladder-name { font-size: 12.5px; font-weight: 700; }
.hladder-here { margin-left: auto; font-size: 10.5px; font-weight: 700; }
.hladder-th { margin-left: auto; font-size: 10px; }

.header {
  height: 6vh;
  width: calc(100% - 20px);
  /* Adjusted typo */
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* Center content vertically */
  align-items: center;
  /* Center content horizontally within the header */
  background-color: var(--bg);
  color: var(--text-primary);
}

.widescreen-padding {
  padding-right: 50px;
  padding-left: 50px;
}

.first-line {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Mobile/tablet: keep the logo + profile button off the viewport edge.
     On widescreen (≥1280px) the parent .header already gets 50px
     widescreen-padding, so reset here to avoid double padding. */
  padding: 0 14px;
  box-sizing: border-box;
}
@media (min-width: 1280px) {
  .first-line { padding: 0; }
}

.logo-section {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.burger-icon {
  padding-left: 12px;
  padding-right: 12px;
  font-size: 20px;
}

.logo-text {
  height: 24px;
  padding-left: 10px;
  /* Adjusted padding for better layout */
}

@media (min-width: 1280px) {
  .logo-text {
    padding-left: 0px;
    /* Adjusted padding for better layout */
  }
}

.logo-text-small {
  height: 18px;
}

.points {
  font-size: 15px;
}

.menu-icon {
  border: none;
  border-radius: 50%;
  /* Makes the button circular */
  margin-left: 20px;
  width: 30px;
  /* Increased size to accommodate badge */
  height: 30px;
  /* Width and height should be equal for a perfect circle */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  /* Remove any default padding */
  position: relative;
  /* Allows proper positioning of the badge */
}

.menu-image {
  width: 100%;
  /* Scale the image to fit within the wrapper */
  height: 100%;
  border-radius: 50%;
  /* Ensure the profile picture is circular */
  object-fit: cover;
  /* Maintains the aspect ratio of the image */
}

/* MemberAvatar slot in the top-right — fills the 30px button perfectly
   and keeps the rank badge anchored to its corner via the parent's
   position:relative. */
.menu-image-wrap {
  width: 100%;
  height: 100%;
}

.rank-image {
  position: absolute;
  /* Position relative to the wrapper */
  bottom: -5px;
  /* Slightly outside the bottom edge */
  right: -5px;
  /* Slightly outside the right edge */
  width: 16px;
  /* Size of the badge */
  height: 16px;
  border-radius: 50%;
  /* Makes the badge circular */
  object-fit: cover;
  /* Maintains the aspect ratio of the badge image */
}

.menu {
  position: absolute;
  top: 50px;
  right: 10px;
  background-color: var(--surface);
  color: var(--text-primary);
  padding: 20px;
  border-radius: 22px;
  border: 1px solid var(--hairline);
  box-shadow: 0 12px 32px -12px rgba(25, 26, 30, 0.18);
  z-index: 100;
  cursor: default;
  width: 195px;
}

nav a {
  display: block;
  color: var(--text-primary);
  text-decoration: none;
  padding: 10px 0;
  transition: color 0.3s;
}

nav a:hover {
  color: var(--accent-strong);
}

.divider {
  margin: 30px;
}

.welcome {
  margin-left: 3px;
  font-size: 16px;
}

.user-info {
  margin-left: 3px;
  font-size: 16px;
}

.version {
  text-align: right;
  font-size: 10px;
  color: var(--text-tertiary);
  padding-bottom: 12px;
}

.action-btn {
  color: var(--text-primary);
  background-color: transparent;
  height: 34px;
  padding-left: 10px;
  text-align: left;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
}

.action-btn:hover {
  background-color: var(--accent-soft-bg);
}

.right-actions {
  gap: 10px;
}

</style>
